import type { HuntTargetDto } from '@/api/hooks'
import { AttemptPips } from '@/components/game/kraken/KrakenPanel'

/**
 * Le marqueur de la bête sur la carte.
 *
 * Ce n'est PAS une île : pas de brouillard à lever, pas de fiche, pas de
 * commerce, et elle change de place quand elle replonge. Elle vit donc sur un
 * champ racine du payload de carte, à côté des îles et non parmi elles, comme
 * l'origine d'un raid entrant.
 *
 * Rendu en HTML absolu (et non en SVG) pour rester cohérent avec les marqueurs
 * d'île du même conteneur transformé : même contre-scale, même zone de tap.
 */
export function KrakenMarker({
  target,
  gridSize,
  k = 1,
  active = false,
  onOpen,
}: {
  target: HuntTargetDto
  gridSize: number
  /** Scale courant du conteneur transformé : le marqueur garde sa taille écran. */
  k?: number
  active?: boolean
  onOpen: () => void
}) {
  const size = 46
  const hit = Math.max(size, 44)

  return (
    <button
      type="button"
      onClick={onOpen}
      title={`${target.label} · ${target.attemptsLeft} tentative(s) restante(s)`}
      data-testid="kraken-marker"
      className="absolute z-10 grid place-items-center bg-transparent"
      style={{
        left: `${(target.x / gridSize) * 100}%`,
        top: `${(target.y / gridSize) * 100}%`,
        width: `${hit}px`,
        height: `${hit}px`,
        transform: `translate(-50%, -50%) scale(${(active ? 1.12 : 1) / k})`,
      }}
    >
      <span
        className="relative grid place-items-center rounded-full"
        style={{
          width: `${size}px`,
          height: `${size}px`,
          background: 'rgba(20,6,3,.82)',
          border: '2px solid #d9584a',
          boxShadow: active
            ? '0 0 0 2.5px #ffb0a4, 0 0 26px 8px rgba(217,88,74,.6)'
            : '0 0 0 2px rgba(217,88,74,.55), 0 0 16px 4px rgba(217,88,74,.45)',
        }}
      >
        <span
          aria-hidden="true"
          className="kraken-sonar pointer-events-none absolute rounded-full"
          style={{ inset: '-9px', border: '1.5px solid rgba(217,88,74,.5)' }}
        />
        <BeastGlyph className="h-[62%] w-[62%] text-[#ffb0a4]" />
      </span>
    </button>
  )
}

/**
 * Glyphe de la bête, lisible à 28 px : une masse et des bras. Le PORTRAIT peint
 * ({@link BeastPortrait}) est réservé au panneau, où il a la place de se lire.
 */
export function BeastGlyph({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
      <path d="M12 4c2.6 0 4.6 2 4.6 4.5S14.6 13 12 13s-4.6-2-4.6-4.5S9.4 4 12 4z" fill="currentColor" fillOpacity=".35" />
      <path d="M8.4 12.2c-1.2 1.8-3 3-5.4 3.6M15.6 12.2c1.2 1.8 3 3 5.4 3.6M10.4 13c-.6 2.2-.6 4 0 6M13.6 13c.6 2.2.8 3.8.2 5.8" />
      <circle cx="10.2" cy="8.2" r=".95" fill="currentColor" stroke="none" />
      <circle cx="13.8" cy="8.2" r=".95" fill="currentColor" stroke="none" />
    </svg>
  )
}

/** Le bandeau de survol : le nom, ce qu'on en sait, et les tentatives restantes. */
export function KrakenMarkerLabel({ target }: { target: HuntTargetDto }) {
  return (
    <div className="flex items-center gap-2 text-[11px]">
      <span className="font-display text-sand-200">{target.label}</span>
      <AttemptPips left={target.attemptsLeft} total={target.maxAttempts} />
    </div>
  )
}
